import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import RegistrationDashboardLayout from './RegistrationDashboardLayout';
import EventContext from '../contexts/EventContext';
import useApiUrlTranslation from './useApiUrlTranslation';
import Loader from './Loader';
import { API_URL } from '../config';
import { CheckCircle, XCircle, Film, PenSquare, Mail, Users, Video, Search, X } from 'lucide-react';

const StatCard = ({ icon, title, value, colorClass }) => (
    <div className="bg-white dark:bg-neutral-900 p-6 rounded-xl shadow-md flex items-center space-x-4">
        <div className={`p-3 rounded-full ${colorClass}`}>{icon}</div>
        <div>
            <p className="text-sm text-gray-500 dark:text-neutral-400">{title}</p>
            <p className="text-2xl font-bold text-gray-800 dark:text-white">{value}</p>
        </div>
    </div>
);

const extractYoutubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|v\/|embed\/|watch\?v=|&v=)([^#&?]{11})/);
    return m ? m[1] : null;
};

const fmt = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// ── Avatar contact ──────────────────────────────────────────────────────────
const Avatar = ({ contact, size = 36 }) => {
    const [broken, setBroken] = useState(false);
    const src = contact?.photos?.small || contact?.photos?.tiny;
    const initials = [contact?.prenom?.[0], contact?.nom?.[0]].filter(Boolean).join('').toUpperCase() || '?';
    const colors = ['bg-e2o-500', 'bg-purple-500', 'bg-amber-500', 'bg-sky-500', 'bg-rose-500'];
    const color = colors[(contact?.id_contact || 0) % colors.length];

    if (src && !broken) {
        return (
            <img
                src={src}
                alt={`${contact?.prenom} ${contact?.nom}`}
                onError={() => setBroken(true)}
                style={{ width: size, height: size }}
                className="rounded-full object-cover shrink-0 border border-gray-200 dark:border-neutral-700"
            />
        );
    }
    return (
        <div
            style={{ width: size, height: size, fontSize: size * 0.38 }}
            className={`${color} rounded-full shrink-0 flex items-center justify-center text-white font-semibold border border-white/20`}
        >
            {initials}
        </div>
    );
};

// ── Badge envoi individuel ──────────────────────────────────────────────────
const SentBadge = ({ lastSent, compact = false }) =>
    lastSent ? (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400 whitespace-nowrap">
            <CheckCircle size={12} />
            {compact ? fmt(lastSent.created_at) : `Envoyé — ${fmt(lastSent.created_at)}`}
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 text-xs text-red-400 dark:text-red-500 whitespace-nowrap">
            <XCircle size={12} /> Non envoyé
        </span>
    );

// ── Carte contact avec statut ──────────────────────────────────────────────
const ContactCard = ({ contact, sentMap }) => {
    const logs = sentMap.get(String(contact?.id_contact)) || [];
    const lastSent = [...logs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    return (
        <div className="flex items-center gap-2.5 py-1.5 first:pt-0 last:pb-0">
            <Avatar contact={contact} size={36} />
            <div className="min-w-0">
                <div className="font-medium text-gray-900 dark:text-white text-sm truncate">
                    {contact?.prenom} {contact?.nom}
                </div>
                {contact?.societe && (
                    <div className="text-xs text-gray-500 dark:text-neutral-400 truncate">{contact.societe}</div>
                )}
                {contact?.mail && (
                    <div className="text-xs text-gray-400 dark:text-neutral-500 truncate">{contact.mail}</div>
                )}
                <SentBadge lastSent={lastSent} compact />
            </div>
        </div>
    );
};

// ── Miniature YouTube ──────────────────────────────────────────────────────
const Thumbnail = ({ youtubeId, titre }) => {
    if (youtubeId) {
        return (
            <a href={`https://youtu.be/${youtubeId}`} target="_blank" rel="noopener noreferrer" className="block shrink-0">
                <img
                    src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`}
                    alt={titre || 'Vidéo'}
                    className="w-28 h-16 object-cover rounded-lg border border-gray-200 dark:border-neutral-700 hover:opacity-80 transition-opacity"
                />
            </a>
        );
    }
    return (
        <div className="w-28 h-16 shrink-0 rounded-lg border border-dashed border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 flex items-center justify-center">
            <Film size={18} className="text-gray-300 dark:text-neutral-600" />
        </div>
    );
};

const EditLink = ({ href }) => (
    <Link
        to={href}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-e2o-500 dark:text-e2o-400 bg-e2o-50 dark:bg-e2o-900/20 border border-e2o-200 dark:border-e2o-800 hover:bg-e2o-100 dark:hover:bg-e2o-900/40 rounded-md transition-colors whitespace-nowrap"
    >
        <PenSquare size={11} /> Éditer
    </Link>
);

// ── Vue par vidéo ─────────────────────────────────────────────────────────
const VideoTable = ({ rows, sentMap, idEvent }) => {
    const withVideo = rows.filter(r => r.video?.id_video?.id_video || r.video?.id_video?.id_youtube);

    if (withVideo.length === 0) {
        return (
            <div className="text-center py-16 text-gray-400 dark:text-neutral-600 text-sm">
                Aucune vidéo uploadée pour le moment.
            </div>
        );
    }

    return (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-neutral-800 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-800 text-sm bg-white dark:bg-neutral-900">
                <thead className="bg-gray-50 dark:bg-neutral-950">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wide w-32">Vidéo</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wide">Titre</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wide">
                            Intervenants &amp; envois
                        </th>
                        <th className="px-4 py-3 w-16" />
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
                    {withVideo.map(({ confEventId, contacts, video }) => {
                        const idVideo = video?.id_video?.id_video;
                        const youtubeId = video?.id_video?.id_youtube || extractYoutubeId(video?.video_url);
                        const titre = video?.id_video?.titre;
                        return (
                            <tr key={confEventId} className="hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition-colors align-top">
                                <td className="px-4 py-3">
                                    <Thumbnail youtubeId={youtubeId} titre={titre} />
                                </td>
                                <td className="px-4 py-3 max-w-xs align-middle">
                                    {titre
                                        ? <span className="text-gray-800 dark:text-neutral-200 leading-snug">{titre}</span>
                                        : <span className="italic text-gray-400 dark:text-neutral-600">Sans titre</span>
                                    }
                                </td>
                                <td className="px-4 py-3">
                                    <div className="divide-y divide-gray-100 dark:divide-neutral-800">
                                        {contacts.map(c => (
                                            <ContactCard key={c?.id_contact} contact={c} sentMap={sentMap} />
                                        ))}
                                    </div>
                                </td>
                                <td className="px-4 py-3 align-middle">
                                    <EditLink href={`/events/${idEvent}/video-edit/${idVideo || confEventId}?tab=delivery`} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

// ── Vue par intervenant ──────────────────────────────────────────────────────
// Chaque ligne = 1 contact (les slots multi-contacts génèrent plusieurs lignes)
const ContactTable = ({ rows, sentMap, idEvent }) => {
    const flat = rows.flatMap(({ confEventId, contacts, video }) => {
        const idVideo = video?.id_video?.id_video;
        const youtubeId = video?.id_video?.id_youtube || extractYoutubeId(video?.video_url);
        const titre = video?.id_video?.titre;
        return contacts.map(c => ({ confEventId, contact: c, idVideo, youtubeId, titre }));
    });

    return (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-neutral-800 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-800 text-sm bg-white dark:bg-neutral-900">
                <thead className="bg-gray-50 dark:bg-neutral-950">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wide">Intervenant</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wide">Titre vidéo</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wide">YouTube</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wide">
                            <Mail size={12} className="inline mr-1" />Email
                        </th>
                        <th className="px-4 py-3 w-16" />
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
                    {flat.map(({ confEventId, contact, idVideo, youtubeId, titre }) => {
                        const logs = sentMap.get(String(contact?.id_contact)) || [];
                        const lastSent = [...logs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
                        return (
                            <tr key={`${confEventId}-${contact?.id_contact}`} className="hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition-colors">
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-2.5">
                                        <Avatar contact={contact} size={36} />
                                        <div className="min-w-0">
                                            <div className="font-medium text-gray-900 dark:text-white truncate">
                                                {contact?.prenom} {contact?.nom}
                                            </div>
                                            {contact?.societe && <div className="text-xs text-gray-500 dark:text-neutral-400 truncate">{contact.societe}</div>}
                                            {contact?.mail && <div className="text-xs text-gray-400 dark:text-neutral-500 truncate">{contact.mail}</div>}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3 max-w-xs">
                                    {titre
                                        ? <span className="text-gray-800 dark:text-neutral-200 line-clamp-2">{titre}</span>
                                        : <span className="italic text-gray-400 dark:text-neutral-600">Sans titre</span>
                                    }
                                </td>
                                <td className="px-4 py-3 text-center whitespace-nowrap">
                                    {youtubeId ? (
                                        <a href={`https://youtu.be/${youtubeId}`} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400 hover:underline">
                                            <Film size={12} /> Voir
                                        </a>
                                    ) : <span className="text-gray-300 dark:text-neutral-700">—</span>}
                                </td>
                                <td className="px-4 py-3 text-center whitespace-nowrap">
                                    <SentBadge lastSent={lastSent} compact />
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <EditLink href={`/events/${idEvent}/video-edit/${idVideo || confEventId}?tab=delivery`} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

// ── Composant principal ───────────────────────────────────────────────────────
const RegistrationDashbordLivraison = ({ isAdmin = true, profile = 'admin' }) => {
    const { currentEvent } = useContext(EventContext);
    const { apiUrlTranslation } = useApiUrlTranslation();

    const [rows, setRows] = useState([]);
    const [sentMap, setSentMap] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('video'); // 'contact' | 'video'
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!currentEvent?.id_event || !apiUrlTranslation) return;

        const fetchAll = async () => {
            setLoading(true);
            setError(null);
            try {
                const participants = await fetch(
                    `${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_event=${currentEvent.id_event} AND cf.statut=143 AND cf.id_conf_event NOT IN (177085, 177140)`
                ).then(r => r.json());

                if (!Array.isArray(participants) || participants.length === 0) {
                    setRows([]);
                    setLoading(false);
                    return;
                }

                const confEventIds = participants.map(p => p.conf_event?.id_conf_event).filter(Boolean);

                const [videos, logsData] = await Promise.all([
                    confEventIds.length > 0
                        ? fetch(`${apiUrlTranslation}?action=getVideosConferences&filter=${encodeURIComponent(`WHERE vc.id_conf_event IN (${confEventIds.join(',')})`)}`).then(r => r.json())
                        : Promise.resolve([]),
                    fetch(`${API_URL}?action=getActivityLogList&action_type=video_link_sent&id_event=${currentEvent.id_event}&limit=1000`).then(r => r.json()).catch(() => []),
                ]);

                const videosByConfEvent = new Map(
                    (Array.isArray(videos) ? videos : []).map(v => [String(v.id_conf_event?.id_conf_event ?? v.id_conf_event), v])
                );

                const map = new Map();
                (Array.isArray(logsData) ? logsData : []).forEach(log => {
                    const key = String(log.id_contact_receiver);
                    if (!map.has(key)) map.set(key, []);
                    map.get(key).push(log);
                });
                setSentMap(map);

                const slotsMap = new Map();
                participants.forEach(p => {
                    const ceId = p.conf_event?.id_conf_event;
                    if (!ceId) return;
                    if (!slotsMap.has(ceId)) {
                        slotsMap.set(ceId, {
                            confEventId: ceId,
                            contacts: [],
                            video: videosByConfEvent.get(String(ceId)),
                        });
                    }
                    slotsMap.get(ceId).contacts.push(p.contact);
                });

                setRows(Array.from(slotsMap.values()));
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [currentEvent?.id_event, apiUrlTranslation]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Filtrage par recherche ──────────────────────────────────────────────
    const filteredRows = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(({ contacts, video }) => {
            const titre = (video?.id_video?.titre || '').toLowerCase();
            return (
                titre.includes(q) ||
                contacts.some(c =>
                    `${c?.prenom} ${c?.nom}`.toLowerCase().includes(q) ||
                    (c?.societe || '').toLowerCase().includes(q) ||
                    (c?.mail || '').toLowerCase().includes(q)
                )
            );
        });
    }, [rows, query]);

    const sentCount = rows.filter(r => r.contacts.some(c => sentMap.has(String(c?.id_contact)))).length;
    const videoCount = rows.filter(r => r.video?.id_video?.id_video || r.video?.id_video?.id_youtube).length;

    return (
        <RegistrationDashboardLayout isAdmin={isAdmin} profile={profile}>
            <div className="p-fib-8 bg-gray-50 dark:bg-neutral-950 min-h-screen">

                {/* En-tête */}
                <header className="mb-fib-8 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 dark:text-white pt-fib-5 leading-phi">
                            Livraison vidéos
                        </h1>
                        <p className="text-gray-600 dark:text-neutral-400 mt-1 leading-phi">
                            {viewMode === 'contact' ? 'Suivi des envois email par intervenant' : 'Catalogue des vidéos uploadées'}
                        </p>
                    </div>

                    {/* Toggle vue */}
                    {!loading && rows.length > 0 && (
                        <div className="flex items-center gap-1 p-1 rounded-lg bg-gray-100 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 self-start mt-fib-5 sm:mt-2 shrink-0">
                            <button
                                onClick={() => setViewMode('video')}
                                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'video'
                                    ? 'bg-white dark:bg-neutral-900 text-gray-900 dark:text-white shadow-sm'
                                    : 'text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-200'}`}
                            >
                                <Video size={13} /> Par vidéo
                            </button>
                            <button
                                onClick={() => setViewMode('contact')}
                                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'contact'
                                    ? 'bg-white dark:bg-neutral-900 text-gray-900 dark:text-white shadow-sm'
                                    : 'text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-200'}`}
                            >
                                <Users size={13} /> Par intervenant
                            </button>

                        </div>
                    )}
                </header>

                {/* Compteurs */}
                {!loading && rows.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                        <StatCard
                            icon={<Users className="h-6 w-6 text-e2o-500" />}
                            title="Intervenants"
                            value={rows.length}
                            colorClass="bg-e2o-100 dark:bg-e2o-950"
                        />
                        <StatCard
                            icon={<Video className="h-6 w-6 text-purple-600" />}
                            title="Vidéos uploadées"
                            value={videoCount}
                            colorClass="bg-purple-100 dark:bg-purple-950"
                        />
                        <StatCard
                            icon={<CheckCircle className="h-6 w-6 text-green-600" />}
                            title="Emails envoyés"
                            value={sentCount}
                            colorClass="bg-green-100 dark:bg-green-950"
                        />
                        <StatCard
                            icon={<XCircle className="h-6 w-6 text-gray-400" />}
                            title="Non envoyés"
                            value={rows.length - sentCount}
                            colorClass="bg-gray-100 dark:bg-neutral-800"
                        />
                    </div>
                )}

                {/* Barre de recherche */}
                {!loading && rows.length > 0 && (
                    <div className="relative mb-5 max-w-md">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500 pointer-events-none" />
                        <input
                            type="text"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Rechercher par nom, société, email ou titre…"
                            className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-gray-800 dark:text-neutral-200 placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-e2o-400 dark:focus:ring-e2o-600 transition"
                        />
                        {query && (
                            <button onClick={() => setQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-neutral-300">
                                <X size={14} />
                            </button>
                        )}
                    </div>
                )}

                {/* Résultat de recherche */}
                {!loading && query && (
                    <p className="text-xs text-gray-400 dark:text-neutral-500 mb-3">
                        {filteredRows.length} résultat{filteredRows.length !== 1 ? 's' : ''} pour « {query} »
                    </p>
                )}

                {/* États */}
                {loading && <div className="flex justify-center py-20"><Loader /></div>}
                {error && <p className="text-red-500 text-sm">{error}</p>}
                {!loading && !error && rows.length === 0 && (
                    <div className="text-center py-20 text-gray-400 dark:text-neutral-600 text-sm">
                        Aucun intervenant trouvé pour cet événement.
                    </div>
                )}
                {!loading && !error && rows.length > 0 && filteredRows.length === 0 && (
                    <div className="text-center py-16 text-gray-400 dark:text-neutral-600 text-sm">
                        Aucun résultat pour « {query} »
                    </div>
                )}

                {/* Tables */}
                {!loading && !error && filteredRows.length > 0 && (
                    viewMode === 'video'
                        ? <VideoTable rows={filteredRows} sentMap={sentMap} idEvent={currentEvent?.id_event} />
                        : <ContactTable rows={filteredRows} sentMap={sentMap} idEvent={currentEvent?.id_event} />
                )}
            </div>
        </RegistrationDashboardLayout>
    );
};

export default RegistrationDashbordLivraison;
